import { useMemo, useState, Suspense, lazy } from 'react' // Import useState, Suspense, lazy
import { Link, useLocation, useNavigate } from 'react-router-dom'

// Import Icon type using type-only import
import type { LucideIcon as Icon } from 'lucide-react' // Use LucideIcon aliased as Icon
import { PanelTop, Users, ShoppingCart } from 'lucide-react' // Use Lucide icons, added ShoppingCart
import {
  Sidebar,
  SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from '@/components/ui/sidebar'

import { NavDocuments } from '@/components/nav-documents'
import { NavMain } from '@/components/nav-main'
import { NavManagement } from '@/components/nav-management'
import { NavSecondary } from '@/components/nav-secondary'
import { NavUser } from '@/components/nav-user'
import { authClient } from '@/lib/auth-client'
import { sidebarData } from '@/data/sidebar-data'
// Import commands data and types
import { actionCommands, type CommandDefinition, type CommandActionContext } from '@/data/commands'
import { useTranslation } from 'react-i18next'

// Lazily import the dialog
const CreateApiKeyDialog = lazy(() => import('@/components/settings/api-keys/create-api-key-dialog'))

// Base NavItem type (icon is optional) - Keep for sidebarData structure
interface NavItem {
	key: string
	url: string
	icon?: Icon
}

// Specific type for Management items (icon is required)
interface ManagementItem extends Omit<NavItem, 'icon'> {
	icon: Icon
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
	const { t } = useTranslation()
	const session = authClient.useSession()
	const isAdmin = session.data?.user?.role === 'admin'
	const location = useLocation() // Get current location
	const navigate = useNavigate() // Get navigate function for action context
	const pathname = location.pathname
	const [dialogToOpen, setDialogToOpen] = useState<string | null>(null) // State to control dialog opening

	// --- Prepare Command Data ---

	const navMainItems: NavItem[] = useMemo(() => {
		const allMainItems = sidebarData.navMain
		if (session.data?.user) {
			return allMainItems
		}
		return allMainItems.filter(item => item.key !== 'sidebar.main.myActivationCodes')
	}, [session.data?.user])

	const managementItems = useMemo((): ManagementItem[] => {
		// Ensure base items conform to ManagementItem if necessary, or handle potential missing icons
		const baseManagementItems = sidebarData.management.filter((item) => item.icon) as ManagementItem[]
		if (isAdmin) {
			return [
			  ...baseManagementItems,
			  { key: 'sidebar.users', url: '/dashboard/admin/users', icon: Users }, // Use Lucide Users
			  { key: 'sidebar.management.orders', url: '/dashboard/admin/orders', icon: ShoppingCart },
			]
	}
	return baseManagementItems
	}, [isAdmin])

	// Create the context for command actions, including openDialog
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const commandActionContext: CommandActionContext = useMemo(
		() => ({
			navigate,
			openDialog: (dialogId) => setDialogToOpen(dialogId), // Implement openDialog
		}),
		[navigate, setDialogToOpen]
	) // Add setDialogToOpen dependency

	// Combine ALL available commands (nav + actions) into CommandDefinition structure
	const allAvailableCommands = useMemo((): CommandDefinition[] => {
		// 1. Transform NavItems to CommandDefinition
		const navCommands: CommandDefinition[] = navMainItems.map((item) => ({
			id: item.url, // Use URL as ID for nav items
			nameKey: item.key,
			type: 'navigation',
			url: item.url,
			icon: item.icon,
			adminOnly: false, // Assume main nav is not admin only
		}))

		// 2. ManagementItems are rendered by NavManagement component, so their commands are not needed here.

		// 3. Filter actionCommands based on admin status
		const availableActionCommands = actionCommands.filter((cmd) => !cmd.adminOnly || isAdmin)

		// 4. Combine all, ensuring no duplicate IDs (prefer actions over nav if IDs clash)
		const combined = [...availableActionCommands]
		const actionIds = new Set(availableActionCommands.map((cmd) => cmd.id))
		// Ensure uniqueNavCommands also fit CommandDefinition structure
		const uniqueNavCommands: CommandDefinition[] = navCommands.filter(
			(cmd) => !actionIds.has(cmd.id)
		)

		return [...combined, ...uniqueNavCommands]
	}, [isAdmin, navMainItems])

	// Calculate suggested commands based on current pathname
	const suggestedCommands = useMemo((): CommandDefinition[] => {
		if (!pathname) return []
		// Filter out suggested items that might already be pinned or recent? No, let suggestions override.
		return allAvailableCommands.filter((cmd) =>
			cmd.contexts?.some((contextPath) => pathname.startsWith(contextPath))
		)
	}, [pathname, allAvailableCommands])

	// --- Render Logic ---
	return (
		<Sidebar collapsible='offcanvas' {...props}>
			<SidebarHeader>
				{/* ... Header content remains the same ... */}
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton asChild className='data-[slot=sidebar-menu-button]:p-2'>
							<Link to='/'>
								<PanelTop className='size-4' />
								<span className='text-base font-semibold'>{t('sidebar.header.companyName')}</span>
							</Link>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarHeader>
			<SidebarContent>
				{/* Pass ALL commands, suggested commands, and context to NavMain */}
				<NavMain
					items={allAvailableCommands} // Pass the unified list
					suggestedItems={suggestedCommands} // Pass suggested list separately
					actionContext={commandActionContext} // Pass context for actions
				/>
				{/* Other Nav components remain the same */}
				{/* <NavDocuments items={sidebarData.documents} /> */}
				<NavManagement isAdmin={isAdmin} items={managementItems} />
				<NavSecondary items={sidebarData.navSecondary} className='mt-auto' />
			</SidebarContent>
			<SidebarFooter>
				<NavUser sessionResult={session} />
			</SidebarFooter>
			{/* Conditionally render the dialog INSIDE the Sidebar structure */}
			{dialogToOpen === 'create-api-key' && (
				<Suspense fallback={null}>
					<CreateApiKeyDialog
						open={dialogToOpen === 'create-api-key'}
						onOpenChange={(isOpen) => {
							if (!isOpen) {
								setDialogToOpen(null) // Close dialog by resetting state
							}
						}}
					/>
				</Suspense>
			)}
		</Sidebar>
	)
}
